// Your account (spec §16.2): name, email, signed-in devices, downloading your
// data, and deleting your account. Plus the page an email-change link opens.
import type { DeletionPreview, MeResponse, SessionInfo } from '../../shared/api.ts';
import { api, ApiError, del, get, patch, post } from '../api.ts';
import { h } from '../util/dom.ts';
import { typedConfirm } from './confirm-dialog.ts';
import { busy, centered, field, formError, getMe, header, legalLinks, longDate, mount, notice, relativeTime, setTitle } from './page.ts';

export async function accountPage(): Promise<void> {
  const me = await getMe();
  setTitle('Account');
  const flash = h('div');
  const section = (title: string, ...children: Node[]) => h('section', { class: 'account-section' }, [h('h2', {}, [title]), ...children]);

  mount(header(me), h('main', { class: 'page narrow' }, [
    h('div', { class: 'page-head' }, [h('h1', {}, ['Account'])]),
    flash,
    h('div', { class: 'stack-lg' }, [
      section('Name', h('p', { class: 'muted' }, ['Shown to people you work with.']), nameForm()),
      section('Email', ...emailForm()),
      section('Devices', ...devices()),
      section('Your data', ...download()),
      section('Delete account', ...deleteAccount()),
    ]),
    legalLinks(),
  ]));

  function nameForm(): HTMLElement {
    const name = h('input', { type: 'text', required: '', maxlength: '80', autocomplete: 'name', value: me.user.name });
    const save = h('button', { type: 'submit', class: 'btn' }, ['Save']);
    const error = formError();
    const form = h('form', { class: 'inline-form' }, [field('Your name', name), save, error]);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      error.clear();
      void busy(save, async () => {
        try {
          const res = await patch<MeResponse>('/api/me', { name: name.value });
          me.user = res.user;
          document.querySelector('.user-name')!.textContent = res.user.name;
          say(['Your name is saved.']);
        } catch (err) {
          error.show(err);
        }
      });
    });
    return form;
  }

  function emailForm(): Node[] {
    const email = h('input', { type: 'email', required: '', autocomplete: 'email', placeholder: 'new@example.com' });
    const send = h('button', { type: 'submit', class: 'btn' }, ['Send confirmation link']);
    const error = formError();
    const form = h('form', { class: 'inline-form' }, [field('New email address', email), send, error]);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      error.clear();
      void busy(send, async () => {
        try {
          await post('/api/me/email', { email: email.value });
          say([`We've sent a link to ${email.value.trim()}. Open it within an hour to finish changing your email.`]);
          email.value = '';
        } catch (err) {
          error.show(err);
        }
      });
    });
    return [h('p', {}, ['You sign in with ', h('strong', {}, [me.user.email]), '.']), form];
  }

  function devices(): Node[] {
    const list = h('ul', { class: 'device-list', role: 'list' }, [h('li', { class: 'muted' }, ['Loading…'])]);
    const signOut = h('button', { type: 'button', class: 'btn' }, ['Sign out everywhere else']);
    const error = formError();
    const load = async () => {
      const sessions = await get<SessionInfo[]>('/api/me/sessions');
      list.replaceChildren(...sessions.map((s) => h('li', {}, [
        h('strong', {}, [`${s.browser} on ${s.os}`]), s.current ? h('span', { class: 'badge' }, ['This device']) : '',
        h('div', { class: 'muted' }, [`Signed in ${longDate(s.signedInAt)} · last active ${relativeTime(s.lastSeenAt)}`]),
      ])));
      signOut.disabled = sessions.length < 2;
    };
    signOut.addEventListener('click', () => void busy(signOut, async () => {
      error.clear();
      try {
        const res = await del<{ ended: number }>('/api/me/sessions');
        say([`Signed out of ${res.ended} other ${res.ended === 1 ? 'device' : 'devices'}.`]);
        await load();
      } catch (err) {
        error.show(err);
      }
    }));
    void load().catch((err: unknown) => error.show(err));
    return [list, signOut, error];
  }

  function download(): Node[] {
    const button = h('button', { type: 'button', class: 'btn' }, ['Download your data']);
    const error = formError();
    button.addEventListener('click', () => void busy(button, async () => {
      error.clear();
      try {
        await saveExport();
      } catch (err) {
        error.show(err);
      }
    }));
    return [
      h('p', { class: 'muted' }, ['A zip of your account details and every board you created, with their images. Each board can be imported again.']),
      button, error,
    ];
  }

  function deleteAccount(): Node[] {
    const button = h('button', { type: 'button', class: 'btn danger-outline' }, ['Delete my account…']);
    const error = formError();
    button.addEventListener('click', () => void busy(button, async () => {
      error.clear();
      try {
        const preview = await get<DeletionPreview>('/api/me/deletion');
        if (preview.blocked) return error.show(new Error(preview.blocked));
        typedConfirm({
          title: 'Delete your account?', confirmText: me.user.email, action: 'Delete my account',
          explain: [
            ["You'll be signed out everywhere, and your name and email address will be erased. This can't be undone."],
            ...(preview.deletes.length ? [[
              'These workspaces will be deleted, with their boards: ',
              preview.deletes.map((w) => `${w.name} (${w.boards} ${w.boards === 1 ? 'board' : 'boards'})`).join(', '), '.',
            ]] : []),
            ...(preview.handsOver.length ? [[
              'These pass to someone else: ', preview.handsOver.map((w) => `${w.name} to ${w.to}`).join(', '), '.',
            ]] : []),
            ['Boards you made in other workspaces stay there. You may want to download your data first.'],
          ],
          run: async () => {
            await api('DELETE', '/api/me', { confirm: me.user.email }, { redirectOn401: false });
            location.assign('/');
          },
        });
      } catch (err) {
        error.show(err);
      }
    }));
    return [h('p', { class: 'muted' }, ['Delete your account and the workspaces only you use.']), button, error];
  }

  function say(content: (Node | string)[]): void {
    flash.replaceChildren(notice(content));
  }
}

/** Downloads the data export as a file (spec §16.2). */
async function saveExport(): Promise<void> {
  const res = await fetch('/api/me/export', { credentials: 'same-origin' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? `Download failed (${res.status}).`);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'atomic-lab-data.zip';
  const url = URL.createObjectURL(await res.blob());
  const a = h('a', { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** `/account/email?token=…`: confirming a new email address. GETs never use tokens, so it takes a click. */
export function confirmEmailPage(): void {
  setTitle('Confirm your new email');
  const token = new URLSearchParams(location.search).get('token') ?? '';
  const confirm = h('button', { type: 'button', class: 'btn primary' }, ['Confirm new email address']);
  const error = formError();
  confirm.addEventListener('click', () => void busy(confirm, async () => {
    error.clear();
    try {
      const res = await post<{ email: string }>('/api/me/email/confirm', { token }, { redirectOn401: false });
      mount(centered(
        h('h1', {}, ['Email address changed']),
        h('p', {}, ['You now sign in with ', h('strong', {}, [res.email]), '.']),
        h('a', { class: 'btn primary', href: '/account' }, ['Go to your account']),
      ));
    } catch (err) {
      error.show(err);
    }
  }));
  mount(centered(
    h('h1', {}, ['Confirm your new email address']),
    h('p', { class: 'muted' }, ['Press the button to start signing in with this address.']),
    error, confirm,
  ));
}
